import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppLogger from 'utils/AppLogger';

import useAxiosPrivate from './useAxiosPrivate';
import useUpdateEffect from './useUpdateEffect';

const useFetchAndParseOnView = ({ url, body, parseData, refProp }) => {
	const axiosPrivate = useAxiosPrivate();

	const [data, setData] = useState(null);
	const [responseData, setResponseData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [hasFetched, setHasFetched] = useState(false);
	const [prevBody, setPrevBody] = useState(body);
	const ref = useRef(null);
	const { t } = useTranslation();

	const [isInView, setIsInView] = useState(false);

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			const response = await axiosPrivate.post(url, body);
			const result = parseData ? parseData(response.data) : response.data;
			setData(result);
			setResponseData(response.data);
			setHasFetched(true);
			setPrevBody(body);
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	}, [axiosPrivate, url, body, parseData]);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry.isIntersecting && !hasFetched) {
					setIsInView(true);
				}
			},
			{ threshold: 0.5 }
		);

		if (ref.current) {
			observer.observe(ref.current);
		}

		if (refProp?.current) {
			observer.observe(refProp.current);
		}

		return () => {
			if (ref.current) {
				observer.unobserve(ref.current);
			}
			if (refProp?.current) {
				observer.unobserve(refProp.current);
			}
		};
	}, []);

	useEffect(() => {
		if (isInView && (!hasFetched || prevBody !== body)) {
			fetchData();
		}
	}, [body, isInView]);

	useUpdateEffect(() => {
		if (responseData) {
			const result = parseData ? parseData(responseData) : responseData;
			setData(result);
		}
	}, [t]);

	return { ref, data, loading };
};

export default useFetchAndParseOnView;
